interface IAddressComponents {
  시?: string
  군?: string
  구?: string
  읍?: string
  면?: string
  동?: string
  리?: string
  가?: string
  로?: string
  길?: string
}

const METROPOLITAN_CITIES = [
  '서울',
  '부산',
  '인천',
  '대구',
  '대전',
  '광주',
  '울산',
] as const

const FIRST_WORDS = ['시', '구', '군'] as const
const SECOND_WORDS = ['읍', '면', '동', '로 ', '리', '가'] as const

const ADDRESS_UNITS = {
  시: '시',
  군: '군',
  구: '구',
  읍: '읍',
  면: '면',
  동: '동',
  리: '리',
  가: '가',
  로: '로',
  길: '길',
} as const

/**
 * 광역시/특별시 매핑
 */
const CITY_ABBREVIATIONS: Record<string, string> = {
  서울특별시: '서울시',
  부산광역시: '부산시',
  대구광역시: '대구시',
  인천광역시: '인천시',
  광주광역시: '광주시',
  대전광역시: '대전시',
  울산광역시: '울산시',
  세종특별자치시: '세종시',
}

/**
 * 도로명에서 '로' 추출
 */
const extractRoadName = (addressPart: string): string | null => {
  if (!addressPart.includes('로')) return null

  const roIndex = addressPart.lastIndexOf('로')
  const extracted = addressPart.substring(0, roIndex + 1)

  return extracted.match(/^\d/) ? null : extracted
}

/**
 * 주소 부분이 특정 단위로 끝나는지 확인
 */
const checkAddressUnit = (addressPart: string, unit: string): boolean => {
  // 숫자로 시작하면 제외
  if (addressPart.match(/^\d/)) return false

  // 특별 처리
  if (unit === '시' && addressPart.includes('광역시')) return false
  if (unit === '로') {
    // '로'로 끝나지 않으면 도로명이 아님
    if (!addressPart.endsWith('로')) return false
    return extractRoadName(addressPart) !== null
  }
  if (unit === '길')
    return addressPart.endsWith('길') && !addressPart.match(/^\d/)

  return addressPart.endsWith(unit)
}

/**
 * 주소에서 구성 요소 추출
 */
const extractComponents = (addressParts: string[]): IAddressComponents => {
  return Object.entries(ADDRESS_UNITS).reduce((result, [key, unit]) => {
    const found = addressParts?.find(part => checkAddressUnit(part, unit))

    if (found) {
      // 도로명 특별 처리
      if (unit === '로') {
        const extracted = extractRoadName(found)
        if (extracted) result[key as keyof IAddressComponents] = extracted
      } else {
        result[key as keyof IAddressComponents] = found
      }
    }

    return result
  }, {} as IAddressComponents)
}

/**
 * 업체 주소 포맷팅
 *
 * 조건:
 * - city + 도로명(로/길) or 동/읍/면까지 표기
 * - city: 구가 있는 경우 구까지 표기
 * - 광역시/특별시는 줄임표현 사용
 *
 * @param address 원본 주소
 * @returns 포맷팅된 주소
 *
 * @example
 * formatPlaceAddress("서울특별시 강남구 신사동") // "서울시 강남구 신사동"
 * formatPlaceAddress("서울특별시 강남구 테헤란로 123") // "서울시 강남구 테헤란로"
 * formatPlaceAddress("대구광역시 북구 원미동") // "대구시 북구 원미동"
 * formatPlaceAddress("경기도 성남시 분당구 정자동") // "성남시 분당구 정자동"
 * formatPlaceAddress("세종특별자치시 한누리대로 2130") // "세종시 한누리대로"
 */
export const formatPlaceAddress = (address: string): string => {
  // '도' 제거 및 공백 기준으로 분리
  const addressParts = address
    ?.split(' ')
    .filter(Boolean)
    .filter(part => !part.endsWith('도')) // 경기도, 강원도 등 '도' 제거

  // 특별시/광역시 정규화 처리
  const normalizedParts = addressParts?.map(part => {
    // 특별시/광역시/특별자치시를 찾아서 변환
    const cityMatch = Object.entries(CITY_ABBREVIATIONS)?.find(
      ([fullName]) => part === fullName
    )

    return cityMatch ? cityMatch[1] : part
  })

  const components = extractComponents(normalizedParts)

  // city 부분 구성
  const cityParts: string[] = []

  if (components.시) {
    cityParts.push(components.시)
  }

  if (components.군) {
    cityParts.push(components.군)
  }

  if (components.구) {
    cityParts.push(components.구)
  }

  const city = cityParts.join(' ')

  // 세 번째 요소 결정 (도로명 우선)
  const thirdElement =
    components.로 ||
    components.길 ||
    components.동 ||
    components.읍 ||
    components.면 ||
    components.가 ||
    ''

  // 최종 포맷팅
  if (city && thirdElement) {
    return `${city} ${thirdElement}`
  }

  // 포맷팅 실패 시 원본 주소 반환
  return address
}

/**
 * 광역시, 특별시, 특별자치시 여부 확인
 */
const isMetropolitan = (region1DepthName: string): boolean =>
  METROPOLITAN_CITIES.some(metro => region1DepthName.includes(metro))

/**
 * 특정 단어로 끝나는 첫 번째 요소 찾기
 */
const findWordEndsWith = (
  addressParts: string[],
  suffixes: readonly string[]
): string | undefined => {
  for (const suffix of suffixes) {
    const found = addressParts.find(part => part.endsWith(suffix))
    if (found) return found
  }
  return undefined
}

/**
 * 타이틀 주소 포맷팅
 */
export const formatAddress = (addressName: string) => {
  if (!addressName) return ''

  const addressParts = addressName.split(' ')

  if (isMetropolitan(addressParts[0])) {
    // 광역시/특별시: 구 + 동 형식
    const gu = findWordEndsWith(addressParts, ['구'])
    const secondWord = findWordEndsWith(addressParts, ['동', '로'])

    return `${gu} ${secondWord}`
  }

  // 일반 시/군 지역
  let firstWord = findWordEndsWith(addressParts, FIRST_WORDS)
  // 세종특별자치시 -> 세종시로 변경
  if (firstWord?.includes('세종')) {
    firstWord = '세종시'
  }
  const secondWord = findWordEndsWith(addressParts, SECOND_WORDS)

  return `${firstWord} ${secondWord}`
}

/**
 *
 * @param addressName
 * 동 이름 표기
 * 동 이름 없을 경우 읍 > 면 > 리 > 가 순으로 표기
 */
export const formatReservationTopNavigationAddress = (addressName: string) => {
  if (!addressName) return ''

  const addressParts = addressName.split(' ')

  const dongName = findWordEndsWith(addressParts, [
    '동',
    '읍',
    '면',
    '리',
    '가',
  ])

  return dongName
}
